import './styles/brand.css';
import './styles/app.css';
import { applyTheme } from './app/prefs';
import { startRouter, navigate } from './app/router';
import { registerServiceWorker } from './app/sw-client';
import { OFFICE_ENABLED, SITE_URL } from './config';
import { detectLang, setLang, t } from './i18n';
import { LANGS, type Route, enabledTools, getTool, homePath, routePath, translateRoute } from './registry';
import { h } from './ui/dom';
import { homePage, licensesPage, notFoundPage, privacyPage } from './ui/pages';
import { footer, header } from './ui/shell';
import { type PageHandle, toolPage } from './ui/toolPage';

applyTheme();

const app = document.getElementById('app')!;
let current: PageHandle | null = null;

function setMeta(title: string, description: string, route: Route) {
  document.title = title;
  document.querySelector('meta[name="description"]')?.setAttribute('content', description);
  const canonical = document.querySelector('link[rel="canonical"]');
  if (canonical && route.kind !== 'notFound') canonical.setAttribute('href', SITE_URL + routePath(route));
  for (const l of LANGS) document.querySelector(`link[rel="alternate"][hreflang="${l}"]`)?.setAttribute('href', SITE_URL + routePath(translateRoute(route, l)));
}

function render(route: Route) {
  if (route.kind === 'root') {
    const lang = detectLang(navigator.languages?.length ? navigator.languages : [navigator.language]);
    navigate(homePath(lang), { replace: true });
    return;
  }
  const lang = route.lang;
  setLang(lang);
  document.documentElement.lang = lang;
  document.querySelector('link[rel="manifest"]')?.setAttribute('href', `/manifest.${lang}.webmanifest`);
  const d = t();

  current?.dispose();
  current = null;

  let content: HTMLElement;
  switch (route.kind) {
    case 'home':
      content = homePage(lang);
      setMeta(d.meta.homeTitle, d.meta.homeDescription, route);
      break;
    case 'tool': {
      const meta = getTool(route.tool);
      if (!enabledTools(OFFICE_ENABLED).includes(meta)) {
        content = notFoundPage(lang);
        break;
      }
      current = toolPage(meta, lang);
      content = current.el;
      setMeta(`${d.tools[meta.id].metaTitle} ${d.meta.toolTitleSuffix}`, d.tools[meta.id].metaDescription, route);
      break;
    }
    case 'page':
      content = route.page === 'privacy' ? privacyPage(lang) : licensesPage();
      setMeta(`${d.pages[route.page].title} ${d.meta.toolTitleSuffix}`, d.pages[route.page].metaDescription, route);
      break;
    default:
      content = notFoundPage(lang);
      document.title = `${d.pages.notFound.title} ${d.meta.toolTitleSuffix}`;
  }

  app.replaceChildren(header(route, lang), h('main', { id: 'main', tabindex: -1 }, content), footer(lang));
}

startRouter(render);
registerServiceWorker();
